// Los cajones de acción del mock: cinco primarios —contacto, compromiso, pago, link, reestructura—
// y «Más acciones» con las otras siete. No conectan a nada: al confirmar cierran y avisan que la
// pantalla sigue pendiente, igual que el mockup.
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Modal, Button } from '@/kit';
import { SideDrawer } from '@/shared/patterns/SideDrawer';
import { cn } from '@/lib/cn';
import type { AgendaDrawerKey, CollectorAgendaRow } from '../types';

const LABEL =
    'mb-1 mt-3 block text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const FIELD =
    'h-8 w-full rounded border border-border bg-card px-2 text-[12px] text-foreground outline-none focus-visible:border-ring';
const TEXTAREA =
    'min-h-16 w-full rounded border border-border bg-card px-2 py-1.5 text-[12px] text-foreground outline-none focus-visible:border-ring';
const OPTION_CARD =
    'w-full rounded-md border border-border p-3 text-left transition-colors hover:bg-accent/50';

const NOTE_TONE = {
    info: 'bg-info-soft text-info',
    warning: 'bg-warning-soft text-warning',
    success: 'bg-success-soft text-success',
} as const;

function Note({ tone, children }: { tone: keyof typeof NOTE_TONE; children: ReactNode }) {
    return <div className={cn('rounded-md p-3 text-[12px]', NOTE_TONE[tone])}>{children}</div>;
}

/** El título de cada cajón, tal cual el mock. */
const DRAWER_TITLE: Record<AgendaDrawerKey, string> = {
    contact: 'Registrar contacto',
    promise: 'Registrar compromiso de pago',
    payment: 'Registrar pago en efectivo',
    link: 'Enviar link de pago',
    restructure: 'Proponer reestructura',
    more: 'Más acciones',
};

/** Lo que dice el aviso al confirmar cada cajón. Los dos sin pie no confirman nada. */
const CONFIRM_MESSAGE: Partial<Record<AgendaDrawerKey, string>> = {
    contact: 'Contacto registrado · queda en historial del crédito',
    promise: 'Compromiso registrado · aparecerá en agenda hasta cumplimiento',
    payment: 'Pago registrado con folio · desbloqueo automático si aplica',
    restructure: 'Propuesta enviada al Admin SK para aprobación',
};

/** Los cuatro modos de envío del cajón «Enviar link de pago». */
const LINK_OPTIONS: readonly {
    key: string;
    title: string;
    chip: string;
    chipTone: string;
    body: string;
    confirmTitle: string;
    confirmBody: string;
}[] = [
    {
        key: 'sms',
        title: 'SMS · plantilla del ciclo',
        chip: 'Recordatorio',
        chipTone: 'border-border bg-muted text-muted-foreground',
        body: 'Texto ajustado al día de mora (M4 §6.3). Incluye link corto para pagar en línea.',
        confirmTitle: 'SMS enviado',
        confirmBody:
            'Recordatorio enviado por SMS. Se registra como contacto "enviado" en la bitácora.',
    },
    {
        key: 'oxxo',
        title: 'Referencia OXXO',
        chip: 'Efectivo',
        chipTone: 'border-warning-soft-border bg-warning-soft text-warning',
        body: 'Genera referencia por el monto de la cuota vencida. Vigencia 72 h. Se envía por SMS + chat.',
        confirmTitle: 'Referencia OXXO enviada',
        confirmBody:
            'Referencia con vigencia 72 h enviada por SMS y chat. Se registra en la bitácora.',
    },
    {
        key: 'app',
        title: 'Link a la App Fiado',
        chip: 'PCF manual',
        chipTone: 'border-info-soft-border bg-info-soft text-info',
        body: 'Abre la app en la pantalla de pago del crédito. Requiere saldo PCF suficiente.',
        confirmTitle: 'Link a App enviado',
        confirmBody:
            'El cliente recibe deep-link a la pantalla de pago del crédito. Requiere saldo PCF. Se registra en la bitácora.',
    },
    {
        key: 'whatsapp',
        title: 'WhatsApp con plantilla',
        chip: 'Directo',
        chipTone: 'border-success-soft-border bg-success-soft text-success',
        body: 'Solo si el cliente aceptó ese canal. Plantilla verificada según ciclo.',
        confirmTitle: 'WhatsApp enviado',
        confirmBody:
            'Plantilla verificada enviada al canal preferido del cliente. Se registra en la bitácora.',
    },
];

/** Las siete opciones del cajón «Más acciones». La primera abre otro cajón; el resto solo avisan. */
const MORE_ACTIONS: readonly {
    key: string;
    title: string;
    body: string;
    opens?: AgendaDrawerKey;
    noticeTitle?: string;
    noticeBody?: string;
}[] = [
    {
        key: 'restructure',
        title: 'Proponer reestructura',
        body: 'Máx 1 por crédito. Aprueba Admin SK.',
        opens: 'restructure',
    },
    {
        key: 'manual-lock',
        title: 'Solicitar bloqueo manual',
        body: 'Solo Admin SK. Casos excepcionales.',
        noticeTitle: 'Solicitar bloqueo manual',
        noticeBody: 'Solo para casos excepcionales. Requiere motivo y queda en auditoría.',
    },
    {
        key: 'manual-unlock',
        title: 'Solicitar desbloqueo manual',
        body: 'Solo Admin SK. Con motivo obligatorio.',
        noticeTitle: 'Solicitar desbloqueo manual',
        noticeBody: 'Ej. pago confirmado por canal no automatizado. Requiere motivo.',
    },
    {
        key: 'investigation',
        title: 'Abrir investigación de pago',
        body: 'M4 §3.3. Cliente reporta pago no aplicado.',
        noticeTitle: 'Abrir investigación',
        noticeBody:
            'Registra una disputa de pago. SLA 48 hrs. Beneficio de la duda: desbloqueo inmediato.',
    },
    {
        key: 'sim-exception',
        title: 'Abrir excepción · cambio de SIM',
        body: 'Cliente perdió chip o cambió operador. Actualiza teléfono y libera bloqueo por número inválido.',
        noticeTitle: 'Excepción · cambio de SIM',
        noticeBody:
            'Se abre una excepción operativa para reemplazo de SIM del cliente. Requiere motivo y evidencia. Aprueba supervisor de cobranza.',
    },
    {
        key: 'escalate',
        title: 'Escalar a supervisor',
        body: 'Casos con amenaza, dispersión geográfica o sin voluntad de pago.',
        noticeTitle: 'Escalar a supervisor',
        noticeBody:
            'El caso pasa al supervisor de cobranza con toda la bitácora. Se sugiere para amenaza, número duplicado o cliente ausente.',
    },
    {
        key: 'reassign',
        title: 'Reasignar cobrador',
        body: 'Balancea carga por zona o escala a senior.',
        noticeTitle: 'Reasignar cobrador',
        noticeBody: 'Reasigna el crédito a otro cobrador de la zona.',
    },
];

/** El folio del cajón de pago, derivado del crédito para que no muestre el de otro. */
function paymentReference(creditId: string): string {
    return `SK-T042-${creditId.replace('-', '')}-2627W28`;
}

function OptionCard({
    title,
    body,
    chip,
    chipTone,
    onClick,
}: {
    title: string;
    body: string;
    chip?: string;
    chipTone?: string;
    onClick: () => void;
}) {
    return (
        <button type="button" className={OPTION_CARD} onClick={onClick}>
            <div className="flex items-center justify-between gap-2">
                <div className="text-[13px] font-medium">{title}</div>
                {chip === undefined ? null : (
                    <span
                        className={cn(
                            'inline-flex items-center whitespace-nowrap rounded-full border px-[5px] py-px text-[9px] font-medium',
                            chipTone,
                        )}
                    >
                        {chip}
                    </span>
                )}
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">{body}</div>
        </button>
    );
}

export interface AgendaDrawersProps {
    open: AgendaDrawerKey | null;
    onOpenChange: (drawer: AgendaDrawerKey | null) => void;
    row: CollectorAgendaRow | null;
}

export function AgendaDrawers({ open, onOpenChange, row }: AgendaDrawersProps) {
    const [notice, setNotice] = useState<{ title: string; body: string } | null>(null);

    const close = () => {
        onOpenChange(null);
    };

    const confirm = () => {
        const message = open === null ? undefined : CONFIRM_MESSAGE[open];
        close();
        setNotice({ title: message ?? 'Confirmado', body: 'Simulación del mockup.' });
    };

    const footer = (
        <>
            <Button size="sm" variant="secondary" onClick={close}>
                Cancelar
            </Button>
            <Button size="sm" onClick={confirm}>
                Confirmar
            </Button>
        </>
    );

    return (
        <>
            <SideDrawer
                open={open !== null && open !== 'link' && open !== 'more'}
                onClose={close}
                title={open === null ? '' : DRAWER_TITLE[open]}
                footer={footer}
            >
                {open === 'contact' ? (
                    <div className="space-y-3">
                        <Note tone="info">
                            Cada contacto queda en historial. Contact center (llamada/SMS/chat) o
                            voz automatizada. Horario permitido 8:00-21:00 hrs.
                        </Note>
                        <label className={LABEL} htmlFor="agenda-contact-channel">
                            Canal
                        </label>
                        <select id="agenda-contact-channel" className={FIELD} defaultValue="call">
                            <option value="call">Llamada telefónica</option>
                            <option value="sms">SMS</option>
                            <option value="chat">Chat App Fiado</option>
                            <option value="whatsapp">WhatsApp</option>
                            <option value="voice_blaster">Voz automatizada (blaster)</option>
                        </select>
                        <label className={LABEL} htmlFor="agenda-contact-outcome">
                            Resultado
                        </label>
                        <select id="agenda-contact-outcome" className={FIELD} defaultValue="spoke">
                            <option value="spoke">Habló con cliente</option>
                            <option value="promise">Promesa de pago</option>
                            <option value="no_answer">No contesta</option>
                            <option value="wrong_number">Número equivocado</option>
                            <option value="voicemail">Buzón de voz</option>
                        </select>
                        <label className={LABEL} htmlFor="agenda-contact-note">
                            Nota (máx 200 caracteres)
                        </label>
                        <textarea
                            id="agenda-contact-note"
                            maxLength={200}
                            placeholder="Contexto de la conversación o siguiente paso"
                            className={TEXTAREA}
                        />
                        <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-md border border-border p-2 hover:bg-accent/40">
                            <input
                                type="checkbox"
                                aria-label="Marcar como incidente"
                                className="mt-0.5"
                            />
                            <div>
                                <div className="text-[12px] font-medium">Marcar como incidente</div>
                                <div className="text-[10.5px] text-muted-foreground">
                                    Amenaza, agresión verbal, número duplicado o cualquier evento
                                    inusual. Se escala al supervisor de cobranza.
                                </div>
                            </div>
                        </label>
                    </div>
                ) : null}

                {open === 'promise' ? (
                    <div className="space-y-3">
                        <Note tone="info">
                            Al llegar la fecha comprometida sin pago → compromiso pasa a incumplido
                            y aparece con prioridad máxima. Cobrador tiene 24h para nuevo contacto.
                        </Note>
                        <label className={LABEL} htmlFor="agenda-promise-day">
                            Fecha del compromiso
                        </label>
                        <input
                            id="agenda-promise-day"
                            type="date"
                            className={cn(FIELD, 'tabular-nums')}
                        />
                        <label className={LABEL} htmlFor="agenda-promise-amount">
                            Monto prometido (MXN)
                        </label>
                        <input
                            id="agenda-promise-amount"
                            type="number"
                            placeholder="Puede ser cuota completa o parcial"
                            className={cn(FIELD, 'tabular-nums')}
                        />
                        <label className={LABEL} htmlFor="agenda-promise-channel">
                            Canal comprometido
                        </label>
                        <select id="agenda-promise-channel" className={FIELD} defaultValue="store">
                            <option value="store">Efectivo en tienda</option>
                            <option value="app">App Fiado (PCF manual)</option>
                            <option value="oxxo">OXXO / farmacia (con referencia)</option>
                            <option value="pcf_auto">PCF automático (esperando saldo)</option>
                        </select>
                        <label className={LABEL} htmlFor="agenda-promise-note">
                            Nota
                        </label>
                        <textarea
                            id="agenda-promise-note"
                            placeholder="Contexto del compromiso"
                            className={TEXTAREA}
                        />
                    </div>
                ) : null}

                {open === 'payment' ? (
                    <div className="space-y-3">
                        <Note tone="success">
                            El folio se genera automáticamente. Desbloqueo del dispositivo es
                            inmediato si cubre la regla (M4 §2.3). Conciliación diaria contra
                            remisión VentasLuga.
                        </Note>
                        <label className={LABEL} htmlFor="agenda-payment-reference">
                            Folio (auto-generado)
                        </label>
                        <input
                            id="agenda-payment-reference"
                            type="text"
                            readOnly
                            value={row === null ? '' : paymentReference(row.creditId)}
                            className={cn(FIELD, 'font-mono')}
                        />
                        <label className={LABEL} htmlFor="agenda-payment-amount">
                            Monto recibido (MXN)
                        </label>
                        <input
                            id="agenda-payment-amount"
                            type="number"
                            placeholder="0.00"
                            className={cn(FIELD, 'tabular-nums')}
                        />
                        <label className={LABEL} htmlFor="agenda-payment-note">
                            Nota (opcional)
                        </label>
                        <textarea
                            id="agenda-payment-note"
                            placeholder="Contexto del pago"
                            className={TEXTAREA}
                        />
                    </div>
                ) : null}

                {open === 'restructure' ? (
                    <div className="space-y-3">
                        <Note tone="warning">
                            Máx 1 reestructura por crédito (M2 §8). Propuesta va al Admin SK para
                            aprobación. Cuotas vencidas se consolidan en nuevo plan francés.
                        </Note>
                        <label className={LABEL} htmlFor="agenda-restructure-kind">
                            Tipo
                        </label>
                        <select
                            id="agenda-restructure-kind"
                            className={FIELD}
                            defaultValue="extension"
                        >
                            <option value="extension">Extensión de plazo</option>
                            <option value="consolidation">Consolidación de vencidas</option>
                        </select>
                        <label className={LABEL} htmlFor="agenda-restructure-term">
                            Nuevo plazo (semanas)
                        </label>
                        <input
                            id="agenda-restructure-term"
                            type="number"
                            placeholder="Ej. 20"
                            className={cn(FIELD, 'tabular-nums')}
                        />
                        <label className={LABEL} htmlFor="agenda-restructure-reason">
                            Justificación (obligatoria)
                        </label>
                        <textarea
                            id="agenda-restructure-reason"
                            placeholder="Ej. Cliente perdió empleo. Tiene voluntad de pago. Propone cuotas más bajas por más tiempo."
                            className={TEXTAREA}
                        />
                    </div>
                ) : null}
            </SideDrawer>

            <SideDrawer open={open === 'link'} onClose={close} title={DRAWER_TITLE.link}>
                <div className="space-y-2">
                    <Note tone="info">
                        Al enviar, el mensaje sale desde la plataforma (no del número del cobrador)
                        y se registra automáticamente en el historial de contacto.
                    </Note>
                    {LINK_OPTIONS.map((option) => (
                        <OptionCard
                            key={option.key}
                            title={option.title}
                            body={option.body}
                            chip={option.chip}
                            chipTone={option.chipTone}
                            onClick={() => {
                                close();
                                setNotice({ title: option.confirmTitle, body: option.confirmBody });
                            }}
                        />
                    ))}
                </div>
            </SideDrawer>

            <SideDrawer open={open === 'more'} onClose={close} title={DRAWER_TITLE.more}>
                <div className="space-y-2">
                    {MORE_ACTIONS.map((action) => (
                        <OptionCard
                            key={action.key}
                            title={action.title}
                            body={action.body}
                            onClick={() => {
                                if (action.opens !== undefined) {
                                    onOpenChange(action.opens);
                                    return;
                                }
                                close();
                                setNotice({
                                    title: action.noticeTitle ?? action.title,
                                    body: action.noticeBody ?? '',
                                });
                            }}
                        />
                    ))}
                </div>
            </SideDrawer>

            <Modal
                isOpen={notice !== null}
                onClose={() => {
                    setNotice(null);
                }}
                title={notice?.title ?? ''}
                size="sm"
            >
                <div className="text-[13px] leading-relaxed">{notice?.body}</div>
                <div className="flex justify-end">
                    <Button
                        size="sm"
                        onClick={() => {
                            setNotice(null);
                        }}
                    >
                        Entendido
                    </Button>
                </div>
            </Modal>
        </>
    );
}
